import axios from 'axios';

// Auto-detect API URL based on environment
const getApiUrl = () => {
  // If explicitly set in environment variables (highest priority)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // If running in development mode (local development)
  if (process.env.NODE_ENV === 'development') {
    return 'http://127.0.0.1:8002';
  }
  
  // For production deployment on Azure/VM
  // Use the same hostname as the frontend but port 8002
  const protocol = window.location.protocol; // http: or https:
  const hostname = window.location.hostname;  // VM IP or domain
  
  // If localhost, use 127.0.0.1
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://127.0.0.1:8002';
  }
  
  // Otherwise use the same host with port 8002
  return `${protocol}//${hostname}:8002`;
};

// Base API configuration
const API_BASE_URL = getApiUrl();

// Default authentication key (should match backend AUTHENTICATION_KEY)
const DEFAULT_AUTH_KEY = '4bd6852c-0162-40c3-9930-148617d5ea39';

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased to 60 seconds to handle slow operations
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${DEFAULT_AUTH_KEY}`, // Always include the auth header with Bearer prefix
  },
});

// Request interceptor to add authentication headers
api.interceptors.request.use(
  (config) => {
    // Always ensure we have the authorization header with Bearer prefix
    if (!config.headers.Authorization) {
      const token = localStorage.getItem('authToken') || DEFAULT_AUTH_KEY;
      // Add Bearer prefix if not already present
      config.headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response?.status === 401) {
      // For authentication errors, we can try with the default key
      console.warn('Authentication failed, ensure your auth key is correct');
    }
    
    // Format error message — detail may be an object (e.g. 409 conflict body)
    const detail = error.response?.data?.detail;
    const errorMessage = (typeof detail === 'string' ? detail : detail?.message) ||
                        error.response?.data?.message ||
                        error.message ||
                        'An unexpected error occurred';

    return Promise.reject({
      ...error,
      message: errorMessage,
      status: error.response?.status,
      // Expose the raw detail object so callers can inspect it (e.g. 409 duplicate)
      details: typeof detail === 'object' && detail !== null ? detail : null,
      isDuplicate: error.response?.status === 409,
    });
  }
);

// Authentication helper
export const setAuthToken = (token) => {
  if (token) {
    // Store token with Bearer prefix if not already present
    const tokenToStore = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    localStorage.setItem('authToken', tokenToStore);
  } else {
    localStorage.removeItem('authToken');
  }
};

export const getAuthToken = () => {
  const token = localStorage.getItem('authToken') || DEFAULT_AUTH_KEY;
  // Always return with Bearer prefix
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
};

// Export BASE_URL for mongodbService and other services
export const BASE_URL = API_BASE_URL;

// Export getAuthHeaders function for fetch-based services
export const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json',
    'Authorization': getAuthToken(),
  };
};

export default api; 